import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog, openDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {useDeletePostponeJobsMutation} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {testIds, vehiclesRoutes} from '@dms/routes';
import {DmsDataGrid, Main, refreshDmsDatagrid} from '@dms/shared';
import {PostponeDialog} from '@dms/shared-aftersales';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

const PREDEFINED_COLUMNS = ['vehicleId', 'postponeReason', 'jobPostponedUntil'] as const;

export function PostponedRequests() {
  const navigate = useNavigate();
  const [deletePostponeJobs] = useDeletePostponeJobsMutation();

  const handleDataGridAction: ActionCallback<typeof PREDEFINED_COLUMNS> = (action) => {
    match(action.actionKey)
      .with('redirectDetail', () => {
        const vehicleId = action.getColumnData<string>('vehicleId', {isOptional: true})?.value;

        if (!vehicleId) {
          return;
        }

        navigate(
          composePath(vehiclesRoutes.postponedJobs, {
            params: {
              id: vehicleId,
            },
            isSearchParamsPreserved: false,
          })
        );
      })
      .with('postponeUntil', () => {
        // Both cells are nil for jobs that were never postponed — open the dialog empty.
        const reason = action.getColumnData<string>('postponeReason', {isOptional: true})?.value;
        const postponedUntil = action.getColumnData<string>('jobPostponedUntil', {
          isOptional: true,
        })?.value;

        openDialog(
          <PostponeDialog
            refreshDataGrid={() => {
              action.deselectAll();
              refreshDmsDatagrid('postpone-job');
            }}
            dialogId="postpone-dialog"
            jobsIds={action.rowId}
            reason={reason}
            postponeUntil={postponedUntil}
            data-testid={testIds.workshop.postponedRequests('postpone-dialog')}
          />,
          {
            id: 'postpone-dialog',
            title: i18n.t('entity.serviceCase.labels.postpone'),
            size: 'small',
            withAdditionalFooter: true,
          }
        );
      })
      .with('delete', () => {
        openDeleteDialog({
          onConfirm: () => {
            deletePostponeJobs({
              body: !Array.isArray(action.rowId)
                ? [{postponeJobId: action.rowId}]
                : action.rowId.map((id) => ({postponeJobId: id})),
            })
              .unwrap()
              .then(() => {
                action.deselectAll();
                refreshDmsDatagrid('postpone-job');
              })
              .catch(() => showNotification.error());
          },
        });
      })
      .otherwise(() => {
        showNotification.error(`Action ${action.actionKey} not implemented`);
      });
  };

  return (
    <>
      <Helmet title={i18n.t('page.workshop.labels.postponedRequests')} />
      <Main
        heading={i18n.t('page.workshop.labels.postponedRequests')}
        data-testid={testIds.workshop.postponedRequests('page')}
        isFullHeight
      >
        <DmsDataGrid
          gridCode="postpone-job"
          actionCallback={handleDataGridAction}
          predefinedColumns={PREDEFINED_COLUMNS}
          data-testid={testIds.workshop.postponedRequests('page')}
        />
      </Main>
    </>
  );
}
